import { Form, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

export interface CategoryFormValues {
    name: string;
    description: string;
}

interface CategoryFormModalProps {
    open: boolean;
    /** The values of the category being renamed; `null` when creating a new one. */
    initialValues: CategoryFormValues | null;
    saving: boolean;
    onSubmit: (values: CategoryFormValues) => void;
    onCancel: () => void;
}

const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 255;

/** Name and description of a user category: the only part of it that is not a list of pages. */
const CategoryFormModal = ({
    open,
    initialValues,
    saving,
    onSubmit,
    onCancel,
}: CategoryFormModalProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm<CategoryFormValues>();

    return (
        <Modal
            open={open}
            title={
                initialValues
                    ? t('configuration.permissions.editCategory')
                    : t('configuration.permissions.newCategory')
            }
            okText={t('configuration.permissions.saveCategory')}
            cancelText={t('configuration.permissions.cancel')}
            confirmLoading={saving}
            onOk={() => form.submit()}
            onCancel={onCancel}
            destroyOnHidden
        >
            {/* Remounted on every open (destroyOnHidden), so initialValues is read each time. */}
            <Form
                form={form}
                layout="vertical"
                initialValues={initialValues ?? { name: '', description: '' }}
                onFinish={onSubmit}
            >
                <Form.Item
                    name="name"
                    label={t('configuration.permissions.name')}
                    rules={[
                        {
                            required: true,
                            whitespace: true,
                            message: t('configuration.permissions.nameRequired'),
                        },
                    ]}
                >
                    <Input maxLength={NAME_MAX_LENGTH} data-testid="category-name" />
                </Form.Item>
                <Form.Item name="description" label={t('configuration.permissions.description')}>
                    <Input.TextArea
                        rows={2}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        data-testid="category-description"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CategoryFormModal;
